import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Pencil, Trash2, X, Save } from 'lucide-react';

const CATEGORIES = [
  { value: 'languages', label: 'Linguagens' },
  { value: 'databases', label: 'Bancos de Dados' },
  { value: 'frameworks', label: 'Frameworks & Ferramentas' },
  { value: 'devops', label: 'DevOps & Ferramentas' }
];

const emptySkill = { category: 'languages', name: '', level: 50, is_primary: false, sort_order: 0, is_active: true };

const SkillsAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySkill);
  const [filter, setFilter] = useState('all');

  const fetch = useCallback(async () => {
    const { data } = await supabase.from('skills').select('*').order('category').order('sort_order');
    setItems(data || []);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    const payload = { ...form };
    delete payload.id;
    if (editing === 'new') {
      await supabase.from('skills').insert(payload);
    } else {
      await supabase.from('skills').update(payload).eq('id', editing);
    }
    setEditing(null);
    fetch();
  };

  const remove = async (id) => {
    if (!window.confirm('Remover esta skill?')) return;
    await supabase.from('skills').delete().eq('id', id);
    fetch();
  };

  const filtered = filter === 'all' ? items : items.filter(s => s.category === filter);

  if (editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">{editing === 'new' ? 'Nova Skill' : 'Editar Skill'}</h1>
          <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-6 max-w-lg">
          <div>
            <label className="block text-gray-300 text-xs font-semibold uppercase mb-2">Categoria</label>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400">
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-gray-300 text-xs font-semibold uppercase mb-2">Nome</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400" placeholder="Spring Boot" />
          </div>
          <div>
            <label className="block text-gray-300 text-xs font-semibold uppercase mb-2">Nível ({form.level}%)</label>
            <input type="range" min="0" max="100" value={form.level} onChange={e => setForm({...form, level: parseInt(e.target.value)})}
              className="w-full accent-lime-400" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-300 text-xs font-semibold uppercase mb-2">Ordem</label>
              <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400" />
            </div>
            <label className="flex items-center space-x-2 pt-6"><input type="checkbox" checked={form.is_primary} onChange={e => setForm({...form, is_primary: e.target.checked})} className="w-4 h-4 accent-lime-400" /><span className="text-gray-300 text-sm">Primária</span></label>
            <label className="flex items-center space-x-2 pt-6"><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="w-4 h-4 accent-lime-400" /><span className="text-gray-300 text-sm">Ativa</span></label>
          </div>
          <div className="flex space-x-4">
            <button onClick={save} className="px-8 py-3 bg-lime-400 text-black font-bold rounded-full hover:bg-lime-300 flex items-center space-x-2"><Save size={18} /><span>Salvar</span></button>
            <button onClick={() => setEditing(null)} className="px-8 py-3 border border-gray-600 text-gray-300 rounded-full hover:border-gray-400">Cancelar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Skills</h1>
        <button onClick={() => { setEditing('new'); setForm({...emptySkill, sort_order: items.length + 1}); }} className="flex items-center space-x-2 px-4 py-2 bg-lime-400 text-black font-bold rounded-full hover:bg-lime-300 text-sm">
          <Plus size={18} /><span>Nova</span>
        </button>
      </div>
      <div className="flex space-x-2 mb-6 flex-wrap gap-2">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === 'all' ? 'bg-lime-400 text-black' : 'bg-gray-800 text-gray-400'}`}>Todas</button>
        {CATEGORIES.map(c => <button key={c.value} onClick={() => setFilter(c.value)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === c.value ? 'bg-lime-400 text-black' : 'bg-gray-800 text-gray-400'}`}>{c.label}</button>)}
      </div>
      <div className="space-y-2">
        {filtered.map(item => (
          <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <div className="w-12 text-center"><span className={`text-xs font-bold px-2 py-1 rounded ${item.is_primary ? 'bg-lime-400/20 text-lime-400' : 'bg-gray-800 text-gray-500'}`}>{item.level}%</span></div>
              <div className="flex-1">
                <p className={`font-semibold text-sm ${item.is_primary ? 'text-lime-400' : 'text-white'}`}>{item.name}</p>
                <p className="text-gray-500 text-xs">{CATEGORIES.find(c => c.value === item.category)?.label}</p>
              </div>
              <div className="w-32 bg-gray-800 rounded-full h-1.5"><div className={`h-full rounded-full ${item.is_primary ? 'bg-lime-400' : 'bg-gray-600'}`} style={{width: `${item.level}%`}} /></div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <button onClick={() => { setEditing(item.id); setForm({...item}); }} className="p-2 text-gray-400 hover:text-lime-400"><Pencil size={14} /></button>
              <button onClick={() => remove(item.id)} className="p-2 text-gray-400 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SkillsAdmin;
